import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import TeamLeaderHandoverSchema, {
  InFlightRevisionDisposition,
  TeamLeaderHandoverJSON,
  TeamLeaderHandoverParams,
  WorkTeamSide,
} from "work/work-agreements/teams/leader-handover/_schemas/team-leader-handover-schema";

export { InFlightRevisionDisposition, WorkTeamSide };
export type { TeamLeaderHandoverJSON, TeamLeaderHandoverParams };

/**
 * A team leader's offer to hand their team over to somebody already on it, and who has said yes so
 * far (#5836).
 *
 * The successor always accepts before anything changes. On hired work nobody else is asked. While a
 * proposal is negotiated, a vendor team handover also waits on the leader of the other side; a client
 * team handover never does. The server sends a handover only to the people it concerns, so whoever
 * holds one may read all of it.
 */
export default class TeamLeaderHandover {
  public static get EXAMPLE(): TeamLeaderHandover {
    const legalEntityId = Guid.generate();
    const outgoingLeader = new EntityMember({ legalEntityId, userId: Guid.generate() });
    return new TeamLeaderHandover({
      id: Guid.generate(),
      side: WorkTeamSide.Vendor,
      outgoingLeader,
      successor: new EntityMember({ legalEntityId, userId: Guid.generate() }),
      initiatedBy: outgoingLeader,
      outgoingLeaderStays: true,
      grantSigningAuthority: false,
      revisionDisposition: InFlightRevisionDisposition.None,
      requestedDate: new AHDate(),
    });
  }

  public static fromJSON(json: TeamLeaderHandoverJSON | null | undefined): TeamLeaderHandover | undefined {
    if (isNullOrUndefined(json)) return undefined;
    return new TeamLeaderHandover(TeamLeaderHandoverSchema.deserialize(json));
  }

  /** Every handover in `jsonArray` this build can read; one it cannot read is dropped, not fatal. */
  public static fromJSONArray(jsonArray: unknown[] | null | undefined): TeamLeaderHandover[] {
    if (isNullOrUndefined(jsonArray)) return [];
    return jsonArray
      .filter((json): json is TeamLeaderHandoverJSON => TeamLeaderHandoverSchema.isValidJSON(json))
      .map((json) => new TeamLeaderHandover(TeamLeaderHandoverSchema.deserialize(json)));
  }

  public readonly id: Guid;
  public readonly side: WorkTeamSide;
  public readonly outgoingLeader: EntityMember;
  public readonly successor: EntityMember;
  public readonly initiatedBy: EntityMember;
  public readonly approver?: EntityMember;
  public readonly outgoingLeaderStays: boolean;
  public readonly grantSigningAuthority: boolean;
  public readonly revisionDisposition: InFlightRevisionDisposition;
  public readonly requestedDate: AHDate;
  public readonly successorAcceptedDate?: AHDate;
  public readonly approvedDate?: AHDate;

  /** Whether the successor has still to say yes. */
  public get awaitsSuccessor(): boolean {
    return this.successorAcceptedDate === undefined;
  }

  /** Whether the leader on the other side has still to approve. */
  public get awaitsApproval(): boolean {
    return this.approver !== undefined && this.approvedDate === undefined;
  }

  /** "client team" or "vendor team", for the sentences that name the team. */
  public get teamName(): string {
    return this.side === WorkTeamSide.Client ? "client team" : "vendor team";
  }

  public constructor(params: TeamLeaderHandoverParams) {
    const validated = TeamLeaderHandoverSchema.validate(params);
    this.id = validated.id;
    this.side = validated.side;
    this.outgoingLeader = validated.outgoingLeader;
    this.successor = validated.successor;
    this.initiatedBy = validated.initiatedBy;
    this.approver = validated.approver;
    this.outgoingLeaderStays = validated.outgoingLeaderStays;
    this.grantSigningAuthority = validated.grantSigningAuthority;
    this.revisionDisposition = validated.revisionDisposition;
    this.requestedDate = validated.requestedDate;
    this.successorAcceptedDate = validated.successorAcceptedDate;
    this.approvedDate = validated.approvedDate;
  }

  /** Whether `member` is the person asked to take the team over, and has not answered yet. */
  public awaitsAnswerFrom(member: EntityMember | null | undefined): boolean {
    return this.awaitsSuccessor && this.successor.isEqualTo(member);
  }

  /** Whether `member` is the leader on the other side who has still to approve. */
  public awaitsApprovalFrom(member: EntityMember | null | undefined): boolean {
    return this.awaitsApproval && (this.approver?.isEqualTo(member) ?? false);
  }

  /** Whether `member` offered this handover, and so may withdraw it. */
  public wasOfferedBy(member: EntityMember | null | undefined): boolean {
    return this.initiatedBy.isEqualTo(member);
  }

  public toJSON(): TeamLeaderHandoverJSON {
    return TeamLeaderHandoverSchema.serialize(this);
  }

  public toString(): string {
    return `TeamLeaderHandover (${this.side}: ${this.outgoingLeader.userId} to ${this.successor.userId})`;
  }

  public isEqualTo(other: TeamLeaderHandover | null | undefined): boolean {
    if (!other) return false;
    return (
      this.id.isEqualTo(other.id) &&
      this.successorAcceptedDate?.toJSON() === other.successorAcceptedDate?.toJSON() &&
      this.approvedDate?.toJSON() === other.approvedDate?.toJSON()
    );
  }

  public clone(): TeamLeaderHandover {
    return new TeamLeaderHandover({
      id: this.id.clone(),
      side: this.side,
      outgoingLeader: this.outgoingLeader.clone(),
      successor: this.successor.clone(),
      initiatedBy: this.initiatedBy.clone(),
      approver: this.approver?.clone(),
      outgoingLeaderStays: this.outgoingLeaderStays,
      grantSigningAuthority: this.grantSigningAuthority,
      revisionDisposition: this.revisionDisposition,
      requestedDate: this.requestedDate.clone(),
      successorAcceptedDate: this.successorAcceptedDate?.clone(),
      approvedDate: this.approvedDate?.clone(),
    });
  }
}
